import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Estimate, WeekBins } from '@fruitways/shared';
import { getKv, setKv } from '@/db/database';

export const EMPTY_BINS: WeekBins = [0, 0, 0, 0, 0, 0];

/**
 * A capture in progress. Survives reloads via Dexie so a grower interrupted in the orchard
 * picks up where they left off. Cleared on submit, cancel or log out.
 */
export interface Draft {
  /** Set when editing a stored estimate; otherwise a new capture for the current week. */
  editingId: string | null;
  farmId: string | null;
  seasonId: string | null;
  weekNumber: number;
  weekStart: string;
  /** Variety codes in display order. */
  picked: string[];
  values: Record<string, WeekBins>;
  activeVariety: string | null;
}

interface CaptureState {
  draft: Draft | null;
  /** Start a fresh capture for the given week. */
  start: (week: { weekNumber: number; weekStart: string; seasonId: string }) => void;
  /** Load a stored estimate for editing. */
  startEdit: (estimate: Estimate) => void;
  update: (patch: Partial<Draft> | ((d: Draft) => Partial<Draft>)) => void;
  setBin: (code: string, dayIndex: number, value: number) => void;
  /** Add/remove a variety keeping master order. Returns false if removal needs confirmation. */
  toggleVariety: (code: string, order: string[], confirmedRemove?: boolean) => boolean;
  addVarieties: (codes: string[], order: string[]) => void;
  clear: () => void;
  /** True when there is anything typed that would be lost. */
  hasUnsaved: boolean;
}

const Ctx = createContext<CaptureState | null>(null);
const KV_DRAFT = 'draft';

export function sortByOrder(codes: string[], order: string[]): string[] {
  const idx = new Map(order.map((c, i) => [c, i]));
  return [...codes].sort((a, b) => (idx.get(a) ?? 999) - (idx.get(b) ?? 999));
}

export function sum(bins: WeekBins | undefined): number {
  return (bins ?? EMPTY_BINS).reduce((a, b) => a + b, 0);
}

export function CaptureProvider({ children }: { children: ReactNode }) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const loaded = useRef(false);
  // Mirror for callbacks that must read the latest draft synchronously (toggleVariety).
  const draftRef = useRef<Draft | null>(null);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    void getKv<Draft>(KV_DRAFT).then((d) => {
      if (d) setDraft(d);
      loaded.current = true;
    });
  }, []);

  useEffect(() => {
    if (!loaded.current) return;
    void setKv(KV_DRAFT, draft);
  }, [draft]);

  const start = useCallback<CaptureState['start']>((week) => {
    setDraft({
      editingId: null,
      farmId: null,
      seasonId: week.seasonId,
      weekNumber: week.weekNumber,
      weekStart: week.weekStart,
      picked: [],
      values: {},
      activeVariety: null,
    });
  }, []);

  const startEdit = useCallback<CaptureState['startEdit']>((e) => {
    const values: Record<string, WeekBins> = {};
    for (const l of e.lines) values[l.varietyCode] = [...l.bins] as WeekBins;
    const picked = e.lines.map((l) => l.varietyCode);
    setDraft({
      editingId: e.id,
      farmId: e.farmId,
      seasonId: e.seasonId,
      weekNumber: e.weekNumber,
      weekStart: e.weekStart,
      picked,
      values,
      activeVariety: picked[0] ?? null,
    });
  }, []);

  const update = useCallback<CaptureState['update']>((patch) => {
    setDraft((d) => (d ? { ...d, ...(typeof patch === 'function' ? patch(d) : patch) } : d));
  }, []);

  const setBin = useCallback((code: string, dayIndex: number, value: number) => {
    setDraft((d) => {
      if (!d) return d;
      const next = [...(d.values[code] ?? EMPTY_BINS)] as WeekBins;
      next[dayIndex] = Math.max(0, Math.floor(value) || 0);
      return { ...d, values: { ...d.values, [code]: next } };
    });
  }, []);

  const toggleVariety = useCallback<CaptureState['toggleVariety']>(
    (code, order, confirmedRemove = false) => {
      const cur = draftRef.current;
      if (
        cur?.editingId &&
        cur.picked.includes(code) &&
        sum(cur.values[code]) > 0 &&
        !confirmedRemove
      ) {
        return false;
      }
      setDraft((d) => {
        if (!d) return d;
        if (d.picked.includes(code)) {
          const picked = d.picked.filter((c) => c !== code);
          const values = { ...d.values };
          delete values[code];
          return {
            ...d,
            picked,
            values,
            activeVariety: d.activeVariety === code ? (picked[0] ?? null) : d.activeVariety,
          };
        }
        const picked = sortByOrder([...d.picked, code], order);
        return {
          ...d,
          picked,
          values: { ...d.values, [code]: d.values[code] ?? EMPTY_BINS },
          activeVariety: d.activeVariety ?? code,
        };
      });
      return true;
    },
    [],
  );

  const addVarieties = useCallback<CaptureState['addVarieties']>((codes, order) => {
    setDraft((d) => {
      if (!d || codes.length === 0) return d;
      const picked = sortByOrder([...new Set([...d.picked, ...codes])], order);
      const values = { ...d.values };
      for (const c of codes) values[c] = values[c] ?? EMPTY_BINS;
      return { ...d, picked, values, activeVariety: codes[0] ?? d.activeVariety };
    });
  }, []);

  const clear = useCallback(() => setDraft(null), []);

  const hasUnsaved = Boolean(
    draft && Object.values(draft.values).some((b) => sum(b) > 0) && !draft.editingId
      ? true
      : draft?.editingId
        ? true
        : false,
  );

  const value = useMemo(
    () => ({
      draft,
      start,
      startEdit,
      update,
      setBin,
      toggleVariety,
      addVarieties,
      clear,
      hasUnsaved,
    }),
    [draft, start, startEdit, update, setBin, toggleVariety, addVarieties, clear, hasUnsaved],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCapture(): CaptureState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useCapture outside CaptureProvider');
  return v;
}
